import { Chip } from "@mui/material";
import { X as CloseIcon } from "lucide-react";
import * as React from "react";

import { RobotoTooltip } from "@/shared/components/RobotoTooltip";

import { ContextType } from "../contextType";

import styles from "./ContextChip.module.css";
import { ContextIcon, type ContextIconKind } from "./ContextIcon";

interface ContextChipProps {
  contextType: ContextType;
  label: string;
  tooltip?: string;
  /** Icon to show; defaults to the one for `contextType`. */
  icon?: ContextIconKind;
  onDelete: () => void;
}

/**
 * Context chip with hover-to-delete behavior.
 * On hover, the type icon is replaced with a close button.
 */
export const ContextChip = ({
  contextType,
  label,
  tooltip,
  icon: iconKind = contextType,
  onDelete,
}: ContextChipProps) => {
  const [isHovered, setIsHovered] = React.useState(false);

  const icon = isHovered ? (
    <button
      onClick={onDelete}
      className={styles.deleteButton}
      aria-label="Remove from context"
    >
      <CloseIcon className={styles.chipIconClose} />
    </button>
  ) : (
    <ContextIcon contextType={iconKind} className={styles.chipIcon} />
  );

  const chip = (
    <Chip
      icon={icon}
      label={label}
      size="small"
      className={styles.chip}
      classes={{
        label: styles.chipLabel,
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      sx={{
        "& .MuiChip-icon": {
          marginRight: "0px",
          marginLeft: "0px",
        },
      }}
    />
  );

  if (tooltip) {
    return (
      <RobotoTooltip title={tooltip} placement="top">
        {chip}
      </RobotoTooltip>
    );
  }

  return chip;
};
